import { graphql, useFragment } from "react-relay";
import type { coursePreviewFragment$key } from "./__generated__/coursePreviewFragment.graphql";
import type { CourseCategoryOrientation } from "./__generated__/courseCategoryFragment.graphql";
import { Image } from "@/components/image";
import { Icon } from "@/components/icon";
import { useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Link, useRouteContext } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { CertificateEmitDialog } from "@/dialogs/certificate-emit-dialog";
import type { coursePreview_paywall$key } from "./__generated__/coursePreview_paywall.graphql";

type CoursePreviewProps = {
	course: coursePreviewFragment$key;
	orientation: CourseCategoryOrientation;
};

function CoursePaywall({ course }: { course: coursePreview_paywall$key }) {
	const { session } = useRouteContext({ from: "__root__" });

	const data = useFragment<coursePreview_paywall$key>(
		graphql`
    fragment coursePreview_paywall on Course {
      visibility
      primaryProduct {
        checkoutUrl
      }
    }
  `,
		course,
	);

	if (data.visibility === "MEMBER_ONLY" && !session?.user) {
		return (
			<div className="bg-zinc-950/75 pb-2 font-semibold text-lg/6 px-2 absolute inset-0 flex flex-col items-center justify-center">
				<Icon name="Lock" className="size-6 text-white" />

				<span className="text-center text-balance text-sm/5 mt-3 text-white">
					Você precisa estar logado para acessar este curso.
				</span>

				<div className="grid grid-cols-1 gap-2.5 w-full mt-6 max-w-40">
					<Link to="/login" className="w-full">
						<Button size="sm" variant="outline" className="w-full">
							Entrar
						</Button>
					</Link>

					<Link to="/cadastro" className="w-full">
						<Button size="sm" className="w-full">
							Criar Conta
						</Button>
					</Link>
				</div>
			</div>
		);
	}

	return (
		<div className="bg-zinc-950/75 pb-2 font-semibold text-lg/6 px-2 absolute inset-0 flex flex-col items-center justify-center">
			<Icon name="Lock" className="size-6 text-white" />

			<span className="text-center text-balance text-sm/5 mt-1.5 text-white">
				Você não tem acesso a esse curso
			</span>

			{data.primaryProduct?.checkoutUrl ? (
				<Link
					to={data.primaryProduct.checkoutUrl}
					target="_blank"
					className="w-full mt-1.5 md:max-w-40"
				>
					<Button size="sm" variant="outline" className="w-full">
						Comprar Curso
					</Button>
				</Link>
			) : null}
		</div>
	);
}

export function CoursePreview({ course, orientation }: CoursePreviewProps) {
	const [isHovered, setIsHovered] = useState(false);
	const [certificateDialogOpen, setCertificateDialogOpen] = useState(false);

	const data = useFragment<coursePreviewFragment$key>(
		graphql`
    fragment coursePreviewFragment on Course
    {
      id
      name
      slug
      canView
      landscapeImage {
	      signedUrl
      }
      portraitImage {
        signedUrl
      }
      progress
      completed
      certificateEmission
      primaryProduct {
        enabled
        checkoutUrl
      }
      certificate {
	      id
				file {
					signedUrl
				}
      }

      ...certificateEmitDialogFragment
      ...coursePreview_paywall
    }
  `,
		course,
	);

	const certificateDialog = (
		<CertificateEmitDialog
			open={certificateDialogOpen}
			onClose={() => setCertificateDialogOpen(false)}
			course={data}
		/>
	);

	if (orientation === "LANDSCAPE") {
		// biome-ignore lint/correctness/noNestedComponentDefinitions: i want this
		const Container = ({ children }: { children: React.ReactNode }) => {
			if (!data.canView) {
				return (
					<div className="transition-all relative aspect-video rounded-sm overflow-hidden lg:w-[345px] lg:min-w-[345px] w-[235px] min-w-[235px]">
						{children}
					</div>
				);
			}

			return (
				<Link
					className="transition-all relative aspect-video rounded-sm overflow-hidden lg:w-[345px] lg:min-w-[345px] w-[235px] min-w-[235px]"
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					to="/cursos/$course"
					params={{ course: data.slug }}
				>
					{children}
				</Link>
			);
		};

		return (
			<>
				{certificateDialog}
				<Container>
					{data.canView && isHovered && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: isHovered ? 1 : 0 }}
							exit={{ opacity: 0 }}
							className="bg-zinc-950/40 pb-2 font-semibold text-white text-lg/6 px-2 absolute inset-0 pointer-events-none flex flex-col items-start justify-end"
						>
							<motion.span
								initial={{ translateX: -10 }}
								animate={{ translateX: 0 }}
								exit={{ translateX: -10 }}
							>
								{data.name}
							</motion.span>

							<div className="flex flex-row gap-2 w-full items-center mt-2 mb-2 last:mb-1">
								<div className="w-full h-1 flex flex-row rounded-full flex-1 overflow-hidden bg-card">
									<div
										className="bg-primary"
										style={{ flex: data.progress / 10000 }}
									/>
									<div
										className="flex-row"
										style={{ flex: 1 - data.progress / 10000 }}
									/>
								</div>

								<span className="text-xs/0 text-white">
									{(data.progress / 10000).toLocaleString("pt-BR", {
										style: "percent",
										minimumFractionDigits: 0,
										maximumFractionDigits: 1,
									})}
								</span>
							</div>

							{data.certificateEmission && data.certificate ? (
								<a
									href={data.certificate.file.signedUrl}
									download
									className="pointer-events-auto"
									onClick={(ev) => {
										ev.stopPropagation();
										ev.preventDefault();
										window.open(data.certificate?.file.signedUrl, "_blank");
									}}
								>
									<Button
										variant="outline"
										className="flex flex-row items-center justify-center text-foreground"
									>
										<Icon name="Download" className="size-4" />
										Baixar Certificado
									</Button>
								</a>
							) : null}

							{data.certificateEmission &&
							data.completed &&
							!data.certificate ? (
								<Button
									variant="outline"
									className="pointer-events-auto flex flex-row items-center justify-center text-foreground"
									onClick={(ev: React.MouseEvent<HTMLButtonElement>) => {
										ev.stopPropagation();
										ev.preventDefault();
										setCertificateDialogOpen(true);
									}}
								>
									Emitir Certificado
								</Button>
							) : null}
						</motion.div>
					)}

					{!data.canView && <CoursePaywall course={data} />}

					{data.landscapeImage?.signedUrl ? (
						<Image
							className="aspect-video w-full object-cover"
							src={data.landscapeImage?.signedUrl}
						/>
					) : (
						<div className="aspect-video rounded-sm w-full flex items-center justify-center bg-muted text-muted-foreground">
							<Icon name="CameraOff" className="size-8" />
						</div>
					)}

					{data.progress > 0 && !isHovered && (
						<div className="w-full h-1 flex flex-row bg-card">
							<div
								className="bg-primary"
								style={{ flex: data.progress / 10000 }}
							/>
							<div
								className="flex-row"
								style={{ flex: 1 - data.progress / 10000 }}
							/>
						</div>
					)}
				</Container>
			</>
		);
	}

	// biome-ignore lint/correctness/noNestedComponentDefinitions: i want this
	const Container = ({ children }: { children: React.ReactNode }) => {
		if (!data.canView) {
			return (
				<div className="relative aspect-7/10 transition-all overflow-clip rounded-sm w-[150px] min-w-[150px] lg:w-[200px] lg:min-w-[200px]">
					{children}
				</div>
			);
		}

		return (
			<Link
				className="relative aspect-7/10 transition-all overflow-clip rounded-sm w-[150px] min-w-[150px] lg:w-[200px] lg:min-w-[200px]"
				onMouseEnter={() => setIsHovered(true)}
				onMouseLeave={() => setIsHovered(false)}
				to="/cursos/$course"
				params={{ course: data.slug }}
			>
				{children}
			</Link>
		);
	};

	return (
		<>
			{certificateDialog}
			<AnimatePresence>
				<Container>
					{data.canView && isHovered && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: isHovered ? 1 : 0 }}
							exit={{ opacity: 0 }}
							className="bg-zinc-950/40 pb-2 font-semibold text-white text-lg/6 px-2 absolute inset-0 pointer-events-none flex flex-col items-start justify-end"
						>
							<motion.span
								initial={{ translateX: -10 }}
								animate={{ translateX: 0 }}
								exit={{ translateX: -10 }}
							>
								{data.name}
							</motion.span>

							<div className="flex flex-row gap-2 w-full items-center mt-2 last:mb-1 mb-2">
								<div className="w-full h-1 flex flex-row rounded-full flex-1 overflow-hidden bg-card">
									<div
										className="bg-primary"
										style={{ flex: data.progress / 10000 }}
									/>
									<div
										className="flex-row"
										style={{ flex: 1 - data.progress / 10000 }}
									/>
								</div>

								<span className="text-xs/0 text-white">
									{(data.progress / 10000).toLocaleString("pt-BR", {
										style: "percent",
										minimumFractionDigits: 0,
										maximumFractionDigits: 1,
									})}
								</span>
							</div>

							{data.certificateEmission &&
							data.completed &&
							data.certificate ? (
								<a
									href={data.certificate.file.signedUrl}
									download
									className="pointer-events-auto w-full"
									onClick={(ev) => {
										ev.stopPropagation();
										ev.preventDefault();
										window.open(data.certificate?.file.signedUrl, "_blank");
									}}
								>
									<Button
										variant="outline"
										className="flex flex-row items-center justify-center w-full text-foreground"
									>
										<Icon name="Download" className="size-4" />
										Baixar Certificado
									</Button>
								</a>
							) : null}

							{data.certificateEmission && !data.certificate ? (
								<Button
									color="white"
									className="pointer-events-auto flex flex-row items-center justify-center w-full text-foreground"
									onClick={(ev: React.MouseEvent<HTMLButtonElement>) => {
										ev.stopPropagation();
										ev.preventDefault();
										setCertificateDialogOpen(true);
									}}
								>
									Emitir Certificado
								</Button>
							) : null}
						</motion.div>
					)}

					{!data.canView && <CoursePaywall course={data} />}

					{data.portraitImage?.signedUrl ? (
						<Image
							className="h-full aspect-7/10 object-cover"
							src={data.portraitImage?.signedUrl}
						/>
					) : (
						<div className="h-full aspect-7/10 flex items-center justify-center bg-muted text-muted-foreground">
							<Icon name="CameraOff" className="size-8" />
						</div>
					)}

					{data.progress > 0 && !isHovered && (
						<div className="w-full absolute bottom-0 h-1 flex flex-row bg-card">
							<div
								className="bg-primary"
								style={{ flex: data.progress / 10000 }}
							/>
							<div
								className="flex-row"
								style={{ flex: 1 - data.progress / 10000 }}
							/>
						</div>
					)}
				</Container>
			</AnimatePresence>
		</>
	);
}
